import { useMutation } from '@apollo/client';
import {
  FeatureFlagTypes,
  Frequency,
  WithdrawalMode,
} from '@generated/graphql';
import { getSubAccountName } from '@interfaces/subAccount';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { TransferContext } from '@ovComponents/4-module/withdrawWorkflow/withdrawWorkflowContext';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { updateContextTransfer } from '@utils/updateWorkflowContext';
import { Alert, Box, Stack, Typography, useToast } from 'hephaestus/web';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { UPDATE_SCHEDULED_TRANSFER } from '../depositReview/depositReview.queries';
import { isFullTransfer } from '../withdrawForm/withdrawalMode.utils';
import { getAvailableFundsToWithdrawCents } from '../withdrawForm/withdrawForm';
import { WithdrawalNetSummary } from './WithdrawalNetSummary';
import {
  CREATE_SCHEDULED_WITHDRAW_TRANSFER,
  CREATE_WITHDRAW_ALL_TRANSFER,
  CREATE_WITHDRAW_TRANSFER,
} from './withdrawReview.queries';

export const WithdrawReview = ({
  options,
  onNext,
  dummyWithdraw,
}: {
  options: any;
  onNext: any;
  dummyWithdraw?: any;
  setDummyWithdraw?: any;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'transfer']);
  const { setTransferData, transferData, mode } = useContext(TransferContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const applicableData =
    dummyWithdraw ??
    (Object.keys(transferData).length !== 0 ? transferData : workflowData);
  const isNetWithdrawal = applicableData?.withdrawalMode === WithdrawalMode.Net;
  const usTransferFlowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UsTransferFlow,
  );
  const enableMultiCurrencyWithdrawalTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyWithdrawTransfers,
  );
  const padAgreementsEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.PadAgreements,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const { enqueueToast } = useToast();

  const recordWorkflowTransfers = useCallback(
    async (transfer: { id: string }) => {
      await setWorkflowData((data) => ({
        ...data,
        currentTransferId: transfer.id,
        createdTransfers: [...(data?.createdTransfers ?? []), transfer],
      }));
    },
    [setWorkflowData],
  );
  const recordWorkflowScheduledTransfers = useCallback(
    async (scheduledTransfer: { id: string }) => {
      await setWorkflowData((data) => ({
        ...data,
        createdScheduledTransfers: [
          ...(data?.createdScheduledTransfers ?? []),
          scheduledTransfer,
        ],
      }));
    },
    [setWorkflowData],
  );

  const [createWithdrawTransfer] = useMutation<{
    createWithdrawTransfer: { transfer: { id: string } };
  }>(CREATE_WITHDRAW_TRANSFER);
  const [createScheduledWithdrawTransfer] = useMutation<{
    createScheduledWithdrawTransfer: { scheduledTransfer: { id: string } };
  }>(CREATE_SCHEDULED_WITHDRAW_TRANSFER);
  const [createWithdrawAllTransfer] = useMutation<{
    withdrawAllFromSubAccount: { transfer: { id: string } };
  }>(CREATE_WITHDRAW_ALL_TRANSFER);

  const fullTransfer = useCallback(
    () =>
      !isNetWithdrawal &&
      isFullTransfer(
        applicableData.amountCents ?? 0,
        getAvailableFundsToWithdrawCents(
          applicableData,
          enableMultiCurrencyWithdrawalTransfers,
        ),
      ),
    [applicableData, enableMultiCurrencyWithdrawalTransfers, isNetWithdrawal],
  );

  const stateTaxPercentage = applicableData?.stateTaxPercentage ?? undefined;
  const federalTaxPercentage =
    applicableData?.federalTaxPercentage ?? undefined;
  const federalTaxWithheld =
    (applicableData?.amountCents ?? 0) * (federalTaxPercentage / 100);
  const stateTaxWithheld =
    (applicableData?.amountCents ?? 0) * (stateTaxPercentage / 100);
  const [updateScheduledWithdrawTransfer] = useMutation(
    UPDATE_SCHEDULED_TRANSFER,
    {
      onCompleted: () => {
        const skipDocuments = !padAgreementsEnabled;
        updateContextTransfer({
          value: { ...applicableData, skipDocuments },
          existingContext: applicableData,
          setApplicationContext: setTransferData,
          setWorkflowCompletionContext: setWorkflowData,
        });
      },
    },
  );

  const submit = async () => {
    setLoading(true);
    if (mode === 'edit') {
      await updateScheduledWithdrawTransfer({
        variables: {
          input: {
            scheduledTransferId: applicableData.scheduledTransferId,
            scheduledDate: applicableData.scheduledDate,
            frequency: applicableData.frequency,
            amountCents: applicableData.amountCents,
            bankAccountId: applicableData.bankAccount.id,
            withdrawalReason: applicableData.withdrawalReason,
          },
        },
      });
    } else if (
      applicableData.frequency !== undefined &&
      applicableData.frequency !== Frequency.OneTime
    ) {
      const { data } = await createScheduledWithdrawTransfer({
        variables: {
          input: {
            bankAccountId: applicableData.bankAccount.id,
            subAccountId: applicableData.subAccount.id,
            amountCents: applicableData.amountCents,
            frequency: applicableData.frequency,
            scheduledDate: applicableData.scheduledDate,
            withdrawalReason: applicableData.withdrawalReason,
            usaFederalTaxWithholdingPercentage: federalTaxPercentage,
            usaStateTaxWithholdingPercentage: stateTaxPercentage,
          },
        },
      });
      if (data?.createScheduledWithdrawTransfer) {
        const {
          createScheduledWithdrawTransfer: { scheduledTransfer },
        } = data;
        await recordWorkflowScheduledTransfers(scheduledTransfer);
      }
    } else if (fullTransfer()) {
      const { data } = await createWithdrawAllTransfer({
        variables: {
          input: {
            bankAccountId: applicableData.bankAccount.id,
            subAccountId: applicableData.subAccount.id,
            usaFederalTaxWithholdingPercentage: federalTaxPercentage,
            usaStateTaxWithholdingPercentage: stateTaxPercentage,
          },
        },
      });
      if (data?.withdrawAllFromSubAccount) {
        const {
          withdrawAllFromSubAccount: { transfer },
        } = data;
        await recordWorkflowTransfers(transfer);
      }
    } else if (
      applicableData.frequency === Frequency.OneTime &&
      applicableData?.scheduledDate
    ) {
      const { data } = await createScheduledWithdrawTransfer({
        variables: {
          input: {
            bankAccountId: applicableData.bankAccount.id,
            subAccountId: applicableData.subAccount.id,
            amountCents: applicableData.amountCents,
            frequency: applicableData.frequency,
            scheduledDate: applicableData.scheduledDate,
            withdrawalReason: applicableData.withdrawalReason,
            usaFederalTaxWithholdingPercentage: federalTaxPercentage,
            usaStateTaxWithholdingPercentage: stateTaxPercentage,
            ...(applicableData?.bankAccount?.currency
              ? { currency: applicableData?.bankAccount?.currency }
              : {}),
          },
        },
      });
      if (data?.createScheduledWithdrawTransfer) {
        const {
          createScheduledWithdrawTransfer: { scheduledTransfer },
        } = data;
        await recordWorkflowScheduledTransfers(scheduledTransfer);
      }
    } else {
      const { data } = await createWithdrawTransfer({
        variables: {
          input: {
            amountCents: applicableData.amountCents,
            ...(applicableData.withdrawalMode
              ? { withdrawalMode: applicableData.withdrawalMode }
              : {}),
            bankAccountId: applicableData.bankAccount.id,
            subAccountId: applicableData.subAccount.id,
            withdrawalReason: applicableData.withdrawalReason,
            usaFederalTaxWithholdingPercentage: federalTaxPercentage,
            usaStateTaxWithholdingPercentage: stateTaxPercentage,
            ...(applicableData?.bankAccount?.currency
              ? { currency: applicableData?.bankAccount?.currency }
              : {}),
          },
        },
      });

      if (data?.createWithdrawTransfer) {
        const {
          createWithdrawTransfer: { transfer },
        } = data;
        await recordWorkflowTransfers(transfer);
      }
    }
    await onNext();
    setLoading(false);
    enqueueToast(t('depositReview.withdrawSuccess'), { variant: 'success' });
  };

  const getFullTransferLabel = useCallback(
    (isMultiCurrencyAccount: boolean, currency: CurrencyCodes) => {
      if (isMultiCurrencyAccount) {
        return t('depositReview.fullTransferWithCurrency', {
          currency,
        });
      }

      return t('depositReview.fullTransfer');
    },
    [t],
  );

  const getWithdrawalAmountLabel = useCallback(() => {
    const isMultiCurrencyAccount =
      enableMultiCurrencyWithdrawalTransfers &&
      applicableData?.subAccount?.account?.availableCurrencies?.length > 1;

    const currency =
      applicableData?.bankAccount?.currency ??
      applicableData?.subAccount?.account?.baseCurrency ??
      CurrencyCodes.CAD;

    if (fullTransfer()) {
      return getFullTransferLabel(isMultiCurrencyAccount, currency);
    }

    return `${formatMoneyValue(applicableData?.amountCents)} ${isMultiCurrencyAccount ? currency : ''}`;
  }, [
    applicableData,
    enableMultiCurrencyWithdrawalTransfers,
    fullTransfer,
    getFullTransferLabel,
  ]);

  const amountLabelKey =
    usTransferFlowEnabled || isNetWithdrawal
      ? 'workflowCompletions:depositReview.grossAmount'
      : 'workflowCompletions:depositReview.amount';

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label:
          mode === 'edit'
            ? t('workflowCompletions:depositReview.updateWithdraw')
            : t('workflowCompletions:depositReview.createWithdraw'),
        disabled: loading,
        dataTestId: 'withdraw-review-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackendString(options?.subtitle)}
      </Typography>
      <Stack spacing={3}>
        <Box>
          <Typography variant='labelLarge' color='textSecondary'>
            {t('workflowCompletions:depositReview.fromSubAccount')}
          </Typography>
          <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
            {getSubAccountName(
              translateBackendString,
              applicableData?.subAccount,
            )}
          </Typography>
        </Box>
        <Box>
          <Typography variant='labelLarge' color='textSecondary'>
            {t('workflowCompletions:depositReview.toBankAccount')}
          </Typography>
          <Typography
            variant='bodyLarge'
            sx={{ fontWeight: 600 }}
          >{`${applicableData?.bankAccount?.name} ${applicableData?.bankAccount?.bankAccountNumber}`}</Typography>
        </Box>
        <Box>
          <Typography variant='labelLarge' color='textSecondary'>
            {t(amountLabelKey)}
          </Typography>
          <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
            {getWithdrawalAmountLabel()}
          </Typography>
        </Box>
        {isNetWithdrawal && (
          <WithdrawalNetSummary
            netAmountLabel={applicableData?.netAmountLabel}
            amountCents={applicableData?.amountCents}
            netAmountCents={applicableData?.netAmountCents}
          />
        )}
        {usTransferFlowEnabled &&
          (stateTaxPercentage >= 0 || federalTaxPercentage >= 0) && (
            <>
              <Box>
                <Typography variant='labelLarge' color='textSecondary'>
                  {t('workflowCompletions:depositReview.federalTaxWithheld')} (
                  {federalTaxPercentage}%)
                </Typography>
                <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                  {federalTaxWithheld
                    ? formatMoneyValue(federalTaxWithheld)
                    : formatMoneyValue(federalTaxWithheld, '$', 0, false)}
                </Typography>
              </Box>
              <Box>
                <Typography variant='labelLarge' color='textSecondary'>
                  {t('workflowCompletions:depositReview.stateTaxWithheld')} (
                  {stateTaxPercentage}%)
                </Typography>
                <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                  {stateTaxWithheld
                    ? formatMoneyValue(stateTaxWithheld)
                    : formatMoneyValue(stateTaxWithheld, '$', 0, false)}
                </Typography>
              </Box>
            </>
          )}
        {applicableData?.frequency && (
          <Box>
            <Typography variant='labelLarge' color='textSecondary'>
              {t('workflowCompletions:withdrawReview.frequency')}
            </Typography>
            <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
              {t(`transfer:frequencyOption.${applicableData.frequency}`)}
            </Typography>
          </Box>
        )}
        {applicableData?.scheduledDate && (
          <Box>
            <Typography variant='labelLarge' color='textSecondary'>
              {t('workflowCompletions:withdrawReview.startsOn')}
            </Typography>
            <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
              {applicableData?.scheduledDate}
            </Typography>
          </Box>
        )}
        {applicableData?.withdrawalReason && (
          <Box>
            <Typography variant='labelLarge' color='textSecondary'>
              {t('workflowCompletions:depositReview.reason')}
            </Typography>
            <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
              {t(
                `transfer:withdrawal.withdrawalReasonOptions.${applicableData?.withdrawalReason}`,
              )}
            </Typography>
          </Box>
        )}
        {usTransferFlowEnabled &&
          (stateTaxPercentage >= 0 || federalTaxPercentage >= 0) && (
            <Box>
              <Alert sx={{ mt: 2 }} severity='info' data-testid='tax-message'>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('transfer:withdrawal.usaTaxDisclaimer'),
                  }}
                />
              </Alert>
            </Box>
          )}
      </Stack>
    </WorkflowCompletionForm>
  );
};

export default WithdrawReview;
